import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { 
  TickerTapeGameState, 
  StockTicker, 
  StockDirection,
  GameRound,
  DifficultySettings,
  STOCK_SYMBOLS,
  DDA_CONFIG,
  INITIAL_DIFFICULTY
} from './types';

// Helper to generate random stock tickers for a round
const generateStocks = (count: number): StockTicker[] => {
  const shuffledSymbols = [...STOCK_SYMBOLS].sort(() => Math.random() - 0.5);
  return shuffledSymbols.slice(0, count).map((symbol, index) => ({
    id: `stock-${index}`,
    symbol,
    direction: Math.random() > 0.5 ? 'UP' : 'DOWN'
  }));
};

// DDA Logic
const adjustDifficulty = (accuracy: number, currentDifficulty: DifficultySettings): DifficultySettings => {
  let newDifficulty = { ...currentDifficulty };

  if (accuracy > DDA_CONFIG.ACCURACY_HIGH_THRESHOLD) {
    // Increase difficulty
    newDifficulty.stockCount = Math.min(
      DDA_CONFIG.MAX_STOCKS, 
      currentDifficulty.stockCount + DDA_CONFIG.DIFFICULTY_INCREASE.stockCountIncrease
    );
    newDifficulty.scrollSpeed = Math.min(
      DDA_CONFIG.MAX_SPEED,
      currentDifficulty.scrollSpeed * DDA_CONFIG.DIFFICULTY_INCREASE.speedMultiplier
    );
    // Reduce display time to make ticker move faster (increase difficulty)
    newDifficulty.displayTime = Math.max(
      2, // Minimum 2 seconds
      currentDifficulty.displayTime * 0.9
    );
    console.log('TickerTape DDA: Increasing difficulty', {
      accuracy: Math.round(accuracy * 100) + '%',
      oldDifficulty: currentDifficulty,
      newDifficulty: newDifficulty
    });
  } else if (accuracy < DDA_CONFIG.ACCURACY_LOW_THRESHOLD) {
    // Decrease difficulty
    newDifficulty.stockCount = Math.max(
      DDA_CONFIG.MIN_STOCKS,
      currentDifficulty.stockCount - DDA_CONFIG.DIFFICULTY_DECREASE.stockCountDecrease
    );
    newDifficulty.scrollSpeed = Math.max(
      DDA_CONFIG.MIN_SPEED,
      currentDifficulty.scrollSpeed * DDA_CONFIG.DIFFICULTY_DECREASE.speedMultiplier
    );
    // Increase display time to make ticker move slower (decrease difficulty)
    newDifficulty.displayTime = Math.min(
      10, // Maximum 10 seconds
      currentDifficulty.displayTime * 1.1
    );
    console.log('TickerTape DDA: Decreasing difficulty', {
      accuracy: Math.round(accuracy * 100) + '%',
      oldDifficulty: currentDifficulty,
      newDifficulty: newDifficulty
    });
  } else {
    console.log('TickerTape DDA: Maintaining difficulty', {
      accuracy: Math.round(accuracy * 100) + '%',
      difficulty: currentDifficulty
    });
  }
  // If accuracy is between thresholds, keep difficulty the same

  return newDifficulty;
};

const INITIAL_STATE: TickerTapeGameState = {
  phase: 'instructions',
  currentRound: 1,
  stocks: generateStocks(INITIAL_DIFFICULTY.stockCount),
  playerAnswers: {},
  timeLeft: INITIAL_DIFFICULTY.displayTime,
  difficulty: INITIAL_DIFFICULTY,
  isComplete: false,
  rounds: [],
  totalAccuracy: 0,
  levelStartTime: Date.now(), // Track when level started
};

type TickerTapeAction =
  | { type: 'START_GAME' }
  | { type: 'START_RECALL' }
  | { type: 'SUBMIT_ANSWER'; payload: { stockSymbol: string; direction: StockDirection } }
  | { type: 'UPDATE_TIME'; payload: number }
  | { type: 'COMPLETE_ROUND' }
  | { type: 'NEXT_ROUND' }
  | { type: 'RESET_GAME' }
  | { type: 'END_GAME' };

const tickerTapeReducer = (state: TickerTapeGameState, action: TickerTapeAction): TickerTapeGameState => {
  switch (action.type) {
    case 'START_GAME':
      return {
        ...state,
        phase: 'watching',
        timeLeft: state.difficulty.displayTime,
        levelStartTime: Date.now(), // Reset level timer when game starts
      };

    case 'START_RECALL':
      return {
        ...state,
        phase: 'recalling',
        timeLeft: 30, // 30 seconds to make selections
        playerAnswers: {},
      };

    case 'SUBMIT_ANSWER':
      const newAnswers = {
        ...state.playerAnswers,
        [action.payload.stockSymbol]: action.payload.direction,
      };

      return {
        ...state,
        playerAnswers: newAnswers,
      };

    case 'UPDATE_TIME':
      const newTimeLeft = action.payload;
      
      if (newTimeLeft <= 0) {
        if (state.phase === 'watching') {
          return {
            ...state,
            phase: 'recalling',
            timeLeft: 30,
            playerAnswers: {},
          };
        } else if (state.phase === 'recalling') {
          // When time runs out in recall phase, auto-complete the round
          const correctAnswers = state.stocks.filter(stock => 
            state.playerAnswers[stock.symbol] === stock.direction
          ).length;
          
          const roundAccuracy = correctAnswers / state.stocks.length;

          const currentRound: GameRound = {
            stocks: state.stocks,
            playerAnswers: state.playerAnswers,
            startTime: Date.now() - (state.difficulty.displayTime * 1000),
            endTime: Date.now(),
            accuracy: roundAccuracy,
          };

          const newRounds = [...state.rounds, currentRound];
          const newTotalAccuracy = newRounds.reduce((sum, round) => sum + round.accuracy, 0) / newRounds.length;
          const newDifficulty = adjustDifficulty(roundAccuracy, state.difficulty);

          return {
            ...state,
            phase: 'result',
            timeLeft: 0,
            rounds: newRounds,
            totalAccuracy: newTotalAccuracy,
            difficulty: newDifficulty,
          };
        }
      }

      return {
        ...state,
        timeLeft: Math.max(0, newTimeLeft),
      };

    case 'COMPLETE_ROUND':
      // Calculate accuracy for this round
      const correctAnswers = state.stocks.filter(stock => 
        state.playerAnswers[stock.symbol] === stock.direction
      ).length;
      
      const roundAccuracy = correctAnswers / state.stocks.length;

      const currentRound: GameRound = {
        stocks: state.stocks,
        playerAnswers: state.playerAnswers,
        startTime: Date.now() - (state.difficulty.displayTime * 1000),
        endTime: Date.now(),
        accuracy: roundAccuracy,
      };

      const newRounds = [...state.rounds, currentRound];
      const newTotalAccuracy = newRounds.reduce((sum, round) => sum + round.accuracy, 0) / newRounds.length;

      // Adjust difficulty based on current round accuracy
      const newDifficulty = adjustDifficulty(roundAccuracy, state.difficulty);

      return {
        ...state,
        rounds: newRounds,
        totalAccuracy: newTotalAccuracy,
        difficulty: newDifficulty,
        phase: 'result',
      };

    case 'NEXT_ROUND':
      const nextRoundNumber = state.currentRound + 1;
      
      // End game after 10 rounds or if player wants to stop
      if (nextRoundNumber > 10) {
        return {
          ...state,
          isComplete: true,
        };
      }

      return {
        ...state,
        currentRound: nextRoundNumber,
        phase: 'watching',
        stocks: generateStocks(state.difficulty.stockCount),
        playerAnswers: {},
        timeLeft: state.difficulty.displayTime,
      };

    case 'END_GAME':
      return {
        ...state,
        isComplete: true,
        phase: 'result',
      };

    case 'RESET_GAME':
      return {
        ...INITIAL_STATE,
        stocks: generateStocks(INITIAL_DIFFICULTY.stockCount),
        phase: 'instructions',
      };

    default:
      return state;
  }
};

const TickerTapeContext = createContext<{
  state: TickerTapeGameState;
  dispatch: React.Dispatch<TickerTapeAction>;
  startGame: () => void;
  submitAnswer: (stockSymbol: string, direction: StockDirection) => void;
  completeRound: () => void;
  nextRound: () => void;
  resetGame: () => void;
  endGame: () => void;
} | null>(null);

export const TickerTapeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(tickerTapeReducer, INITIAL_STATE);

  const startGame = useCallback(() => {
    dispatch({ type: 'START_GAME' });
  }, []);

  const submitAnswer = useCallback((stockSymbol: string, direction: StockDirection) => {
    dispatch({ type: 'SUBMIT_ANSWER', payload: { stockSymbol, direction } });
  }, []);

  const completeRound = useCallback(() => {
    dispatch({ type: 'COMPLETE_ROUND' });
  }, []);

  const nextRound = useCallback(() => {
    dispatch({ type: 'NEXT_ROUND' });
  }, []);

  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
  }, []);

  const endGame = useCallback(() => {
    dispatch({ type: 'END_GAME' });
  }, []);

  const value = {
    state,
    dispatch,
    startGame,
    submitAnswer,
    completeRound,
    nextRound,
    resetGame,
    endGame,
  };

  return (
    <TickerTapeContext.Provider value={value}>
      {children}
    </TickerTapeContext.Provider>
  );
};

export const useTickerTape = () => {
  const context = useContext(TickerTapeContext);
  if (!context) {
    throw new Error('useTickerTape must be used within a TickerTapeProvider');
  }
  return context;
};